import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

import { applyTransaction } from './handlers/apply-transaction';
import { createInitialState } from './handlers/create-initial-state';
import { updateView } from './handlers/update-view';
import { SelectorOptions } from './types';

export const createSuggestionPlugin = ({ editor, name, ...options }: SelectorOptions) => {
	const store: any = {
		component: null,
		isClicked: false,
		popup: null,
		props: null,
		timeout: null,
		triggerPos: 0,
	};

	const plugin: Plugin<any> = new Plugin({
		key: new PluginKey(`${name}-suggestion`),

		state: {
			// Initialize the initial state when the plugin instance is created
			init() {
				return createInitialState();
			},

			// Runs whenever a transaction is applied to the editor state
			apply(transaction, prevState, _oldState, newState) {
				return applyTransaction({
					editor,
					name,
					newState,
					prevState,
					transaction,
					...options,
				});
			},
		},

		view() {
			return {
				// Runs whenever the view of the editor is to be updated. Occurs after the
				// state apply() handler
				update: (view, prevState) => {
					updateView({
						editor,
						key: this.key,
						prevState,
						store,
						name,
						view,
						...options,
					});
				},

				destroy: () => {
					if (!store.props) {
						return;
					}

					if (!store.popup[0].state.isDestroyed) {
						store.popup?.[0].destroy();
					}

					store.component?.destroy();
				},
			};
		},

		props: {
			// Call the keydown hook if suggestion is active.
			handleKeyDown(view, event) {
				const { active, range } = plugin.getState(view.state);

				if (!active) {
					return false;
				}

				if (event.key === 'Escape') {
					store.popup?.[0].hide();
				}

				return store.component?.ref?.onKeyDown({ view, event, range }) || false;
			},

			// Setup decorator on the currently active suggestion.
			decorations(state) {
				const { active, range, decorationId } = plugin.getState(state);

				if (!active) {
					return null;
				}

				return DecorationSet.create(state.doc, [
					Decoration.inline(range.from, range.to, {
						nodeName: 'span',
						class: 'suggestion',
						'data-decoration-id': decorationId,
					}),
				]);
			},
		},
	});

	return plugin;
};
